import { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import { AxisBottom, AxisLeft } from '@visx/axis';
import { GridRows } from '@visx/grid';
import { NumberLike } from '@visx/scale';

import {
  PATH_STROKE_DELTA,
  PATH_STROKE_WIDTH,
} from '@components/patterns/LineChart/constants';
import { useLineChartContext } from '@components/patterns/LineChart/context';
import { formatValue } from '@components/patterns/Metric';

function hideOverlappingXTicks(axis: SVGGElement, gap = 12) {
  const ticks = axis.querySelectorAll('g.visx-axis-tick');
  if (ticks.length <= 1) return;

  const rects = Array.from(ticks).map((tick) => tick.getBoundingClientRect());
  const maxWidth = Math.max(...rects.map((r) => r.width));

  // Get lowest common index distance between ticks
  let index = 1;
  while (
    index < rects.length - 1 &&
    rects[0].left + maxWidth + gap > rects[index].left
  ) {
    index++;
  }

  // Only show every nth tick
  ticks.forEach((tick, i) => {
    if (i % index === 0) {
      tick.setAttribute('opacity', '1');
    } else {
      tick.setAttribute('opacity', '0');
    }
  });
}

export interface XAxisProps {
  label?(x: number): string;
}

export function XAxis({ label = formatValue }: XAxisProps) {
  const { series, rect, scales } = useLineChartContext();
  const ref = useRef<SVGGElement>(null);

  const xTickFormat = useCallback(
    (tick: { valueOf(): number }) => {
      const dataPt = series.at(0)?.data.at(tick.valueOf());
      if (!dataPt) return undefined;
      return label(dataPt.x);
    },
    [label, series]
  );

  // Show as many unique, integer ticks as possible
  const xTickValues = useMemo(() => {
    const uniqueValues = new Set<string>();
    const ticks = scales.index.ticks().filter((t) => {
      if (t % 1 !== 0) return false;

      const formatted = xTickFormat(t);
      if (!formatted) return false;

      if (uniqueValues.has(formatted)) return false;
      uniqueValues.add(formatted);
      return true;
    });
    return ticks;
  }, [xTickFormat, scales.index]);

  useLayoutEffect(() => {
    const xAxis = ref.current;
    if (xAxis) {
      hideOverlappingXTicks(xAxis);
    }
  }, [xTickValues, rect.innerWidth]);

  return (
    <AxisBottom
      innerRef={ref}
      axisClassName="x-axis"
      top={rect.marginTop + rect.innerHeight}
      scale={scales.index}
      orientation="bottom"
      hideTicks
      hideAxisLine
      tickValues={xTickValues}
      tickFormat={xTickFormat}
      tickTransform="translate(0 -2)"
      tickLabelProps={{
        textAnchor: 'start',
        fontFamily: 'var(--font-family-base)',
        fill: 'var(--text-secondary)',
      }}
    />
  );
}

export interface YAxisProps {
  ticks?: number;
  label?(y: number): string;
}

export function YAxis({ ticks = 5, label = formatValue }: YAxisProps) {
  const { rect, scales } = useLineChartContext();

  const yTickFormat = useCallback(
    (tick: NumberLike) => {
      return label(tick.valueOf());
    },
    [label]
  );

  // Get the tick values
  const yTickValues = useMemo(() => {
    // If the only two ticks are 0 and 1, just show 0
    const domain = scales.y.domain();
    if (domain[0] === 0 && domain[1] === 1) {
      return [0];
    }
    return scales.y.ticks(ticks).filter((t) => t % 1 === 0);
  }, [scales.y, ticks]);

  return (
    <>
      <GridRows
        scale={scales.y}
        top={-PATH_STROKE_DELTA}
        left={rect.marginLeft + PATH_STROKE_WIDTH}
        width={rect.marginLeft + rect.innerWidth - PATH_STROKE_WIDTH}
        tickValues={yTickValues}
        stroke="rgba(from var(--secondary-40) r g b / 0.2)"
        strokeWidth={PATH_STROKE_WIDTH}
        strokeDasharray="1,2.5"
        strokeLinecap="round"
      />

      <AxisLeft
        axisClassName="y-axis"
        left={rect.marginLeft}
        scale={scales.y}
        tickValues={yTickValues}
        hideTicks
        hideAxisLine
        tickFormat={yTickFormat}
        tickLabelProps={{
          fontFamily: 'var(--font-family-base)',
          fill: 'var(--text-secondary)',
        }}
      />
    </>
  );
}
